<div class="justup-dialog">
  <vl-dialog-header-export
    [exporting]="exporting()"
    (close)="dialogRef.close()"
    (export)="exportExcel()"
  >
    {{ data.title }}
    @if (data.articleFilterName) {
      <span class="caption on-background-alt" style="margin-left: 8px">· {{ data.articleFilterName }}</span>
    }
  </vl-dialog-header-export>

  <div class="justup-dialog-content flex-col gap-16">
    <!-- Granularité toggle -->
    <div class="flex-row justify-center">
      <vl-chips-toggle
        [elements]="scaleElements"
        [value]="scale()"
        (valueChange)="scale.set($event)"
        size="small"
      />
    </div>

    <!-- Stats cards (identiques à repartition-ca-dialog) -->
    <div class="flex-row gap-8 wrap">
      <vl-radio-card mode="display" color="valid" icon="euro" title="Valeur totale" style="flex: 1; min-width: 140px">
        <span content class="headline">{{ formatEuro(data.totalCA) }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display" color="valid" icon="trending_up" title="Marge" style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ formatEuro(data.totalMargin) }}</span>
          <span class="tiny" style="opacity: 0.7">{{ (data.totalMarginPct * 100) | number:'1.1-1' }}%</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="secondary" icon="calendar_month" [title]="scaleLabelPlural[scale()]" style="flex: 1; min-width: 140px">
        <span content class="headline">{{ stats().count }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display" color="warn" icon="leaderboard" title="Concentration 80%" style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <div class="flex-row align-baseline gap-4">
            <span class="headline">{{ stats().pareto80Count }}</span>
            <span class="body-2" style="opacity: 0.7">/ {{ stats().count }}</span>
          </div>
          <span class="tiny" style="opacity: 0.7">représentent 80% du CA</span>
        </div>
      </vl-radio-card>
    </div>

    <!-- Header colonnes -->
    <div class="flex-row gap-8 p-8 align-center"
         style="padding-right: 12px; border-bottom: 1px solid var(--color-on-disabled)">
      <span class="shrink-0" style="width: 16px"></span>
      <span class="grow tiny on-background-alt"
            style="font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        {{ scaleLabel[scale()] }}
      </span>
      <span class="tiny on-background-alt shrink-0"
            matTooltip="Taux de marge = (CA − Coût) / CA"
            style="min-width: 40px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        Marge
      </span>
      <span class="tiny on-background-alt shrink-0"
            matTooltip="Chiffre d'affaires HT"
            style="min-width: 80px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        CA
      </span>
      <span class="tiny on-background-alt shrink-0"
            matTooltip="Part du CA total"
            style="min-width: 45px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        Part
      </span>
    </div>

    <!-- Arbre : période → article → lot (même pattern que repartition-ca-dialog) -->
    @for (row of rows(); track row.key) {
      <!-- LEVEL 0 : Période -->
      <div>
        <div class="flex-row gap-8 p-8 align-center border-box border-8 cursor-pointer"
             [style.border-left]="'3px solid ' + colorForPct(row.marginPct)"
             style="border: 1px solid var(--color-outline)"
             (click)="toggle(row.key, $event)">
          <mat-icon class="on-disabled shrink-0" style="font-size: 16px; width: 16px; height: 16px">
            {{ isOpen(row.key) ? 'expand_more' : 'chevron_right' }}
          </mat-icon>
          <span class="grow caption on-background-alt"
                style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600">
            {{ row.label }}
          </span>
          <span class="tiny shrink-0" [class]="pctClass(row.marginPct)"
                style="min-width: 40px; text-align: right; font-weight: 600">
            {{ (row.marginPct * 100) | number:'1.0-0' }}%
          </span>
          <span class="tiny on-disabled shrink-0"
                style="min-width: 80px; text-align: right">{{ formatEuroExact(row.ca) }}</span>
          <span class="tiny on-accent shrink-0"
                style="min-width: 45px; text-align: right">{{ percentPart(row.ca) }}%</span>
        </div>

        @if (isOpen(row.key)) {
          <div class="flex-col gap-1 mt-2 mb-2" style="margin-left: 24px">
            @for (art of row.articles; track art.key) {
              <!-- LEVEL 1 : Article -->
              <div>
                <div class="flex-row gap-8 p-4 align-center cursor-pointer"
                     (click)="toggle(art.key, $event)">
                  <mat-icon class="on-disabled shrink-0" style="font-size: 14px; width: 14px; height: 14px">
                    {{ isOpen(art.key) ? 'expand_more' : 'chevron_right' }}
                  </mat-icon>
                  <span class="grow tiny on-background-alt"
                        style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
                    {{ art.articleName }}
                  </span>
                  <span class="tiny shrink-0" [class]="pctClass(art.marginPct)"
                        style="min-width: 40px; text-align: right; font-weight: 600">
                    {{ (art.marginPct * 100) | number:'1.0-0' }}%
                  </span>
                  <span class="tiny on-disabled shrink-0"
                        style="min-width: 80px; text-align: right">{{ formatEuroExact(art.ca) }}</span>
                  <span class="tiny on-accent shrink-0"
                        style="min-width: 45px; text-align: right">{{ percentPart(art.ca) }}%</span>
                </div>

                @if (isOpen(art.key) && art.lots.length) {
                  <!-- LEVEL 2 : Lots (pattern identique à repartition-ca-dialog) -->
                  <div class="flex-col gap-2 mt-1 mb-2" style="margin-left: 28px">
                    @for (lot of art.lots; track lot.key) {
                      <div class="flex-row gap-12 p-8 border-on-disabled border-box border-8">
                        <div class="flex-col gap-4 grow" style="min-width: 0">
                          @if (lot.lotId) {
                            <vl-lot-card [lotId]="lot.lotId" mode="text"/>
                          } @else {
                            <span class="caption on-background-alt" style="font-weight: 600">{{ lot.lotNumero }}</span>
                          }
                          <!-- Vente -->
                          <div class="flex-row gap-4 align-baseline wrap">
                            <span class="tiny on-disabled" style="min-width: 60px">Vente</span>
                            <span class="tiny on-background-alt">
                              {{ lot.qty | number:'1.0-0' }} × {{ lot.prixUnitaire | number:'1.2-2' }} €
                            </span>
                            <span class="tiny on-disabled">=</span>
                            <span class="tiny on-background-alt" style="font-weight: 600">
                              {{ lot.ca | number:'1.0-0' }} €
                            </span>
                          </div>
                          <!-- Coût -->
                          <div class="flex-row gap-4 align-baseline wrap">
                            <span class="tiny on-disabled" style="min-width: 60px">Coût</span>
                            <span class="tiny on-background-alt" style="font-weight: 600">
                              {{ lot.cost | number:'1.0-0' }} €
                            </span>
                            <span class="tiny on-disabled">
                              (mat. {{ lot.costMat | number:'1.0-0' }} + mo. {{ lot.costMO | number:'1.0-0' }})
                            </span>
                          </div>
                        </div>
                        <div class="flex-col gap-2 shrink-0 align-end justify-center">
                          <span class="caption" [class]="pctClass(lot.marginPct)" style="font-weight: 700">
                            {{ (lot.marginPct * 100) | number:'1.0-0' }}%
                          </span>
                          <span class="tiny on-disabled">Marge : {{ lot.margin | number:'1.0-0' }} €</span>
                        </div>
                      </div>
                    }
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
</div>
